// One code per team, shared by the team's coaches (dev call, 2026-10-07).
// A coach's verification record carries the code of each team they hold:
//   - coaches/me/ (fetchMyCoachStatus) lists them as teamCodes:
//     [{ teamId, league, teamName, displayName, ageGroup, isPrimary, code }];
//   - users/by-email/ nests the record as coachVerification: the primary
//     team's code in verificationCode, an additional team's on its
//     additionalTeams entry (verificationCode).
// teamCodeFor picks the code of the team a page has open, else the
// primary team's, so a code shown is always the code of a named team.
//
// The squads of a program (17U, 16U, 15U) can share one program code
// (notes of 10/19, D14). A row says so when the server sends it (codeScope
// or scope 'program', programName, codeAgeGroups or ageGroups); else two or
// more of the coach's squads showing the same code are read as that
// program's code. codeCoverageLine names the program and its age groups.

const text = (value) => (typeof value === "string" ? value.trim() : "");
// A squad's name without its age group ("Spartans Elite 16U" -> "Spartans
// Elite"): the program's name. Never empties a name.
const withoutAge = (name) => text(name).replace(/(^|\s)(?:\d{1,2}\s?u|u\s?\d{1,2})(?=\s|$)/gi, " ").replace(/\s{2,}/g, " ").trim() || text(name);
const list = (value) => {
  const items = Array.isArray(value) ? value.map(text).filter(Boolean) : [];
  return items.length ? items : undefined;
};
// A field the row has, else undefined (so a record without it compares as before).
const optional = (value) => text(value) || undefined;

// The program fields of a row, when it carries them.
const programFields = (row) => {
  const scope = text(row.codeScope) || text(row.scope);
  return {
    displayName: optional(row.displayName),
    ageGroup: optional(row.ageGroup),
    scope: scope || undefined,
    programName: optional(row.programName),
    ageGroups: list(row.codeAgeGroups) || list(row.ageGroups),
  };
};

// [{ teamId, league, teamName, isPrimary, code, ... }] from either shape.
export const coachTeamCodes = (record) => {
  if (!record || typeof record !== "object") return [];
  if (Array.isArray(record.teamCodes)) {
    return record.teamCodes
      .filter((row) => row && typeof row === "object")
      .map((row) => ({
        teamId: text(row.teamId),
        league: text(row.league),
        teamName: text(row.teamName),
        isPrimary: !!row.isPrimary,
        code: text(row.code),
        ...programFields(row),
      }));
  }
  const rows = [];
  if (text(record.teamId) || text(record.verificationCode)) {
    rows.push({
      teamId: text(record.teamId),
      league: text(record.league),
      teamName: text(record.currentTeam) || text(record.teamName),
      isPrimary: true,
      code: text(record.verificationCode),
      ...programFields(record),
    });
  }
  (Array.isArray(record.additionalTeams) ? record.additionalTeams : []).forEach((team) => {
    if (!team || typeof team !== "object") return;
    rows.push({
      teamId: text(team.teamId),
      league: text(team.league),
      teamName: text(team.currentTeam),
      isPrimary: false,
      code: text(team.verificationCode) || text(team.teamCode),
      ...programFields(team),
    });
  });
  return rows;
};

// "16U" style age groups, oldest first ("17U, 16U, 15U").
const byAge = (ages) => [...new Set(ages.filter(Boolean))].sort((x, y) => (parseInt(y, 10) || 0) - (parseInt(x, 10) || 0));

// What a row's code covers: its own program fields, else the coach's other
// squads that show the same code (two or more squads of one program, by its
// name without the age group: one program code). Teams of different names
// sharing a code (an old one-code-per-coach record) stay one team's code.
const coverageOf = (rows, hit) => {
  // The server says it is one team's code.
  if (!hit || hit.scope === "team") return {};
  if (hit.scope === "program" || hit.programName || hit.ageGroups) {
    return { programName: hit.programName || withoutAge(hit.teamName) || undefined, ageGroups: hit.ageGroups };
  }
  const sharing = rows.filter((row) => row.code && row.code === hit.code);
  if (sharing.length < 2) return {};
  const names = [...new Set(sharing.map((row) => withoutAge(row.teamName).toLowerCase()).filter(Boolean))];
  if (names.length !== 1) return {};
  const ages = byAge(sharing.map((row) => row.ageGroup || ""));
  return { programName: withoutAge(hit.teamName) || undefined, ageGroups: ages.length ? ages : undefined };
};

// { code, teamName, programName?, ageGroups? } for `team` ({ teamId,
// league, currentTeam }): its own code when the record has one for it, else
// the primary team's (a page with no team open, or a team the coach sees
// through a league grant). teamName is the name people read (with the
// squad's age group when the server sends it).
export const teamCodeFor = (record, team) => {
  const rows = coachTeamCodes(record);
  const teamId = text(team?.teamId);
  const league = text(team?.league);
  const named = (row, fallback = "") => row.displayName || row.teamName || fallback;
  if (teamId) {
    // Squads of one program share an id and differ by league: with several,
    // only the league says which one is open.
    const sameId = rows.filter((row) => row.teamId === teamId && row.code);
    const hit = sameId.find((row) => !league || !row.league || row.league === league) ||
      (sameId.length === 1 ? sameId[0] : null);
    if (hit) return { code: hit.code, teamName: named(hit, text(team?.currentTeam)), ...coverageOf(rows, hit) };
  }
  const primary = rows.find((row) => row.isPrimary);
  return {
    code: primary?.code || text(record?.verificationCode),
    teamName: (primary && named(primary)) || text(record?.currentTeam) || text(record?.teamName),
    ...coverageOf(rows, primary),
  };
};

// "17U, 16U and 15U".
const joinAges = (ages) => (ages.length < 2 ? ages.join("") : `${ages.slice(0, -1).join(", ")} and ${ages[ages.length - 1]}`);

// The line under a code: who it is for and who shares it. A program code
// names the program and its age groups ("For every age group of Oakland
// Soldiers (17U, 16U and 15U). Every coach of the program shares it."), a
// team code its team ("For Oakland Soldiers 16U. Every coach of the team
// shares it."). `lead` starts the line ("For", "Team code for"). "" when
// there is no name to give.
export const codeCoverageLine = (info, lead = "For") => {
  const programName = text(info?.programName);
  const ages = Array.isArray(info?.ageGroups) ? info.ageGroups.filter(Boolean) : [];
  if (programName || ages.length) {
    const name = [programName || "the program", ages.length ? `(${joinAges(ages)})` : ""].filter(Boolean).join(" ");
    return `${lead} every age group of ${name}. Every coach of the program shares it.`;
  }
  const team = text(info?.teamName);
  return team ? `${lead} ${team}. Every coach of the team shares it.` : "";
};
